import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Contacts } from '@capacitor-community/contacts';
import { friendsAPI, userAPI, friendGroupsAPI, temporaryLocationsAPI, visibilityAPI, contactsAPI } from '../services/api';
import { Friend, FriendRequest, User, FriendGroupWithMembers, TemporaryLocationWithFriend, UserSearchResult, ContactSyncStatus } from '../types';
import { hashContacts, RawContact } from '../utils/phoneHash';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import VisibilityBubble from '../components/VisibilityBubble';
import ListColorPicker, { getRandomListColor } from '../components/ListColorPicker';
import AddMembersModal from '../components/AddMembersModal';
import AddToListsModal from '../components/AddToListsModal';
import './Friends.css';

// Tabs order for swipe navigation
const TABS = ['friends', 'groups', 'search'] as const;
type TabType = typeof TABS[number];

// Friends component with group management and friend menu
const Friends: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [canceledRequestIds, setCanceledRequestIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);

  // Get initial tab from URL params (e.g., /friends?tab=search)
  const getInitialTab = (): TabType => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'search') return 'search';
    if (tabParam === 'groups') return 'groups';
    return 'friends';
  };
  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());

  // Swipe handling
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  const tabsRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const minSwipeDistance = 30; // Reduced for easier swipe
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Groups state
  const [groups, setGroups] = useState<FriendGroupWithMembers[]>([]);
  const [showCreateGroupForm, setShowCreateGroupForm] = useState(false);
  const [newGroupData, setNewGroupData] = useState(() => ({ name: '', color: getRandomListColor() }));
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectedFriendIds, setSelectedFriendIds] = useState<number[]>([]);
  const [friendGroups, setFriendGroups] = useState<Map<number, { id: number; name: string; color?: string }[]>>(new Map());

  // Add friend to group modal
  const [showAddFriendToGroupModal, setShowAddFriendToGroupModal] = useState(false);
  const [selectedFriendForGroup, setSelectedFriendForGroup] = useState<number | null>(null);
  const [selectedGroupsForFriend, setSelectedGroupsForFriend] = useState<number[]>([]);
  const [initialGroupsForFriend, setInitialGroupsForFriend] = useState<number[]>([]);
  const [showCreateGroupInModal, setShowCreateGroupInModal] = useState(false);
  const [newGroupInModal, setNewGroupInModal] = useState(() => ({ name: '', color: getRandomListColor() }));

  // Edit group state
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [editGroupData, setEditGroupData] = useState({ name: '', color: '#4a90e2' });
  const [openMenuGroupId, setOpenMenuGroupId] = useState<number | null>(null);

  // Friend menu state
  const [openMenuFriendId, setOpenMenuFriendId] = useState<number | null>(null);

  // Temporary locations state
  const [tempLocations, setTempLocations] = useState<TemporaryLocationWithFriend[]>([]);

  // Friend suggestions state
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [displayedSuggestionsCount, setDisplayedSuggestionsCount] = useState(3);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Contact sync state
  const [contactSyncStatus, setContactSyncStatus] = useState<ContactSyncStatus | null>(null);
  const [contactSyncLoading, setContactSyncLoading] = useState(false);
  const [contactSyncError, setContactSyncError] = useState<string | null>(null);
  const [showContactSyncModal, setShowContactSyncModal] = useState(false);
  const [hideSyncStatusCard, setHideSyncStatusCard] = useState(() => sessionStorage.getItem('hideSyncStatus') === 'true');
  const [contactSyncEnabled, setContactSyncEnabled] = useState(() => localStorage.getItem('contactSyncEnabled') !== 'false');
  const [contactSyncPromptDismissed, setContactSyncPromptDismissed] = useState(false);

  // Autocomplete state
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<UserSearchResult[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [selectedAutocompleteIndex, setSelectedAutocompleteIndex] = useState(-1);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  // Sidebar success message state
  const [sidebarMessage, setSidebarMessage] = useState<string>('');

  // Expanded invitation state (for message and visibility after sending request)
  const [expandedInvitations, setExpandedInvitations] = useState<Map<number, {
    friendshipId: number;
    message: string;
    savedMessage: string; // Last saved message (for cancel functionality)
    visibilityLevel: number;
    initialVisibilityLevel: number;
    isSaving: boolean;
    isSavingVisibility: boolean;
    justSaved: boolean;
    isEditingMessage: boolean; // false = read-only mode, true = editing mode
  }>>(new Map());

  // Accepted requests showing visibility form (before closing)
  const [acceptedRequests, setAcceptedRequests] = useState<Map<number, {
    visibilityLevel: number;
    initialVisibilityLevel: number;
    isSavingVisibility: boolean;
    justSaved: boolean;
    request: FriendRequest; // Keep original request data for display
  }>>(new Map());

  useEffect(() => {
    loadFriends();
    loadRequests();
    loadGroups();
    loadTempLocations();
    loadSuggestions();
    loadContactSyncStatus();
  }, []);

  // Reload data when changing tabs to ensure fresh data
  useEffect(() => {
    setCanceledRequestIds([]);
    // Reload friends and requests to get fresh data
    loadFriends();
    loadRequests();
  }, [activeTab]);

  // Auto-scroll tabs to keep active tab visible
  useEffect(() => {
    if (tabsRef.current) {
      const activeButton = tabsRef.current.querySelector('button.active') as HTMLElement;
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeTab]);

  // Swipe handlers
  const handleSwipe = useCallback(() => {
    const distanceX = touchStartX.current - touchEndX.current;
    const distanceY = touchStartY.current - touchEndY.current;

    // Only swipe if horizontal movement > vertical movement (avoid conflict with scroll)
    if (Math.abs(distanceX) < Math.abs(distanceY)) return;

    const isLeftSwipe = distanceX > minSwipeDistance;
    const isRightSwipe = distanceX < -minSwipeDistance;

    const currentIndex = TABS.indexOf(activeTab);

    if (isLeftSwipe && currentIndex < TABS.length - 1) {
      setActiveTab(TABS[currentIndex + 1]);
    } else if (isRightSwipe && currentIndex > 0) {
      setActiveTab(TABS[currentIndex - 1]);
    }
  }, [activeTab]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  }, []);

  const onTouchEnd = useCallback(() => {
    handleSwipe();
  }, [handleSwipe]);

  // Auto-refresh data every 30 seconds when on friends or search tab
  useEffect(() => {
    if (activeTab === 'friends' || activeTab === 'search') {
      const interval = setInterval(() => {
        loadFriends();
        loadRequests();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Refresh data when window regains focus (e.g., after clicking a notification)
  useEffect(() => {
    const handleFocus = () => {
      loadFriends();
      loadRequests();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.group-menu-container')) {
        setOpenMenuGroupId(null);
      }
    };

    if (openMenuGroupId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuGroupId]);

  // Close friend menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.friend-menu-container')) {
        setOpenMenuFriendId(null);
      }
    };

    if (openMenuFriendId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuFriendId]);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Optimized: Load friends with their groups in a single query (fixes N+1 problem)
  const loadFriends = async () => {
    try {
      const { friends: friendsWithGroups } = await friendsAPI.getFriendsWithGroups();

      // Extract friends and groups from the combined response
      const friendsList: Friend[] = friendsWithGroups.map(({ groups, ...friend }) => friend);
      setFriends(friendsList);

      // Build groups map from the response
      const groupsMap = new Map<number, { id: number; name: string; color?: string }[]>();
      friendsWithGroups.forEach((friend: any) => {
        if (friend.groups && friend.groups.length > 0) {
          groupsMap.set(friend.user_id, friend.groups);
        }
      });
      setFriendGroups(groupsMap);
    } catch (error) {
      console.error('Failed to load friends:', error);
    }
  };

  const loadRequests = async () => {
    try {
      const [pending, sent] = await Promise.all([
        friendsAPI.getPendingRequests(),
        friendsAPI.getSentRequests(),
      ]);
      setPendingRequests(pending.requests);
      setSentRequests(sent.requests);
    } catch (error) {
      console.error('Failed to load requests:', error);
    }
  };

  const loadSuggestions = async () => {
    try {
      setLoadingSuggestions(true);
      const response = await friendsAPI.getFriendSuggestions(50);
      setSuggestions(response.suggestions);
    } catch (error) {
      console.error('Failed to load friend suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleLoadMoreSuggestions = () => {
    setDisplayedSuggestionsCount(prev => Math.min(prev + 10, suggestions.length));
  };

  // Load contact sync status
  const loadContactSyncStatus = async () => {
    try {
      const status = await contactsAPI.getStatus();
      setContactSyncStatus(status);
    } catch (error) {
      console.error('Failed to load contact sync status:', error);
    }
  };

  // Handle contact sync using Capacitor Contacts plugin
  const handleRetrySync = () => {
    setContactSyncError(null);
  };

  const handleContactSync = async () => {
    setContactSyncLoading(true);
    setContactSyncError(null);

    try {
      // Check if running in Capacitor/native context
      const isNative = Capacitor.isNativePlatform();

      if (!isNative) {
        // Web fallback - show message that contacts sync requires mobile app
        setContactSyncError('La synchronisation des contacts nécessite l\'application mobile.');
        setContactSyncLoading(false);
        return;
      }

      // First check current permission status
      const currentStatus = await Contacts.checkPermissions();
      console.log('Current contacts permission status:', currentStatus);

      let permissionStatus = currentStatus;

      // If not granted, request permission
      if (currentStatus.contacts !== 'granted') {
        permissionStatus = await Contacts.requestPermissions();
        console.log('After request, permission status:', permissionStatus);
      }

      if (permissionStatus.contacts !== 'granted') {
        // Permission was denied - show error with settings button
        setContactSyncError('SETTINGS_REQUIRED');
        setContactSyncLoading(false);
        return;
      }

      // Get all contacts with phone numbers
      const result = await Contacts.getContacts({
        projection: {
          name: true,
          phones: true,
        },
      });

      if (!result.contacts || result.contacts.length === 0) {
        setContactSyncError('Aucun contact trouvé sur cet appareil.');
        setContactSyncLoading(false);
        return;
      }

      // Transform contacts to our format
      const rawContacts: RawContact[] = result.contacts
        .filter(c => c.phones && c.phones.length > 0)
        .map(c => ({
          name: c.name?.display || undefined,
          phones: c.phones!.map(p => p.number || '').filter(n => n),
        }));

      if (rawContacts.length === 0) {
        setContactSyncError('Aucun contact avec numéro de téléphone trouvé.');
        setContactSyncLoading(false);
        return;
      }

      // Hash contacts client-side for privacy
      const hashedContacts = await hashContacts(rawContacts);

      if (hashedContacts.length === 0) {
        setContactSyncError('Aucun numéro de téléphone valide trouvé.');
        setContactSyncLoading(false);
        return;
      }

      // Send hashed contacts to server
      const syncResult = await contactsAPI.sync(hashedContacts);

      // Clear hide flag to show the status card again
      sessionStorage.removeItem('hideSyncStatus');
      setHideSyncStatusCard(false);

      // Update status with match info
      setContactSyncStatus({
        synced: true,
        contactCount: syncResult.syncedCount,
        matchesCount: syncResult.matchesCount,
      });

      // Reload suggestions to include contact-based ones
      loadSuggestions();
    } catch (error: any) {
      console.error('Contact sync failed:', error);
      if (error.message?.includes('permission') || error.message?.includes('denied')) {
        setContactSyncError('SETTINGS_REQUIRED');
      } else {
        setContactSyncError('Erreur lors de la synchronisation des contacts.');
      }
    } finally {
      setContactSyncLoading(false);
    }
  };

  // Clear synced contacts (privacy/GDPR)
  const handleClearContacts = async () => {
    try {
      await contactsAPI.clear();
      setContactSyncStatus({ synced: false, contactCount: 0 });
      // Reload suggestions to remove contact-based suggestions
      loadSuggestions();
    } catch (error) {
      console.error('Failed to clear contacts:', error);
    }
  };

  // Debounced autocomplete search
  const searchAutocomplete = useCallback(async (query: string) => {
    if (query.length < 2) {
      setAutocompleteSuggestions([]);
      setShowAutocomplete(false);
      setSelectedAutocompleteIndex(-1);
      return;
    }

    setAutocompleteLoading(true);
    try {
      const { users } = await userAPI.searchUsersAutocomplete(query, 7);
      setAutocompleteSuggestions(users);
      setShowAutocomplete(true);
      setSelectedAutocompleteIndex(-1);
    } catch (error) {
      console.error('Autocomplete search failed:', error);
      setAutocompleteSuggestions([]);
    } finally {
      setAutocompleteLoading(false);
    }
  }, []);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the autocomplete search (300ms)
    debounceTimerRef.current = setTimeout(() => {
      searchAutocomplete(value);
    }, 300);
  };

  const handleAutocompleteSelect = (user: UserSearchResult) => {
    setSearchQuery(`${user.first_name} ${user.last_name}`);
    setShowAutocomplete(false);
    setSelectedAutocompleteIndex(-1);
    // Trigger full search with the selected user
    handleSearchWithResults([user]);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete || autocompleteSuggestions.length === 0) {
      if (e.key === 'Enter') {
        setShowAutocomplete(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedAutocompleteIndex(prev =>
          prev < autocompleteSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedAutocompleteIndex(prev =>
          prev > 0 ? prev - 1 : autocompleteSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedAutocompleteIndex >= 0) {
          handleAutocompleteSelect(autocompleteSuggestions[selectedAutocompleteIndex]);
        } else {
          setShowAutocomplete(false);
        }
        break;
      case 'Escape':
        setShowAutocomplete(false);
        setSelectedAutocompleteIndex(-1);
        break;
    }
  };

  const handleSearchWithResults = (users: UserSearchResult[]) => {
    // Convert UserSearchResult to the format expected by searchResults
    const convertedUsers: User[] = users.map(u => ({
      id: u.id,
      email: u.email,
      first_name: u.first_name,
      last_name: u.last_name,
      profile_photo: u.profile_photo,
      created_at: '',
      updated_at: '',
      address: u.city ? {
        id: 0,
        user_id: u.id,
        street_address: '',
        city: u.city,
        country: u.country || ''
      } : undefined,
      // Store extended info for display
      _searchResult: u
    })) as any;
    setSearchResults(convertedUsers);
  };

  const loadTempLocations = async () => {
    try {
      const { temporaryLocations } = await temporaryLocationsAPI.getFriendsActive();
      setTempLocations(temporaryLocations);
    } catch (error) {
      console.error('Failed to load temporary locations:', error);
    }
  };

  // Helper function to map backend error messages to translation keys
  const getErrorMessage = (error: any, fallbackKey: string): string => {
    const backendError = error.response?.data?.error;

    // Map backend error messages to translation keys
    const errorMap: { [key: string]: string } = {
      'Already friends': 'friends.messages.alreadyFriends',
      'Friend request already sent': 'friends.messages.requestAlreadySent',
      'Cannot send friend request to yourself': 'friends.messages.cannotAddYourself',
    };

    if (backendError && errorMap[backendError]) {
      return t(errorMap[backendError]);
    }

    return t(fallbackKey);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setShowAutocomplete(false);
    setLoading(true);
    try {
      // Use the autocomplete API for better results (includes friendship status)
      const { users } = await userAPI.searchUsersAutocomplete(searchQuery, 20);
      handleSearchWithResults(users);
    } catch (error) {
      console.error('Search failed:', error);
      showMessage(t('friends.messages.searchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (userId: number, defaultVisibility: number = 4) => {
    try {
      const response = await friendsAPI.sendRequest(userId);

      // Show success message in sidebar
      setSidebarMessage(t('friends.messages.requestSent'));
      // Auto-clear after 4 seconds
      setTimeout(() => setSidebarMessage(''), 4000);

      // Remove from canceled list if it was there
      const friendRequest = sentRequests.find(req => req.user_id === userId);
      if (friendRequest) {
        setCanceledRequestIds(canceledRequestIds.filter(id => id !== friendRequest.friendship_id));
      }

      // Open expanded invitation form for this user (in editing mode)
      setExpandedInvitations(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, {
          friendshipId: response.friendship.id,
          message: '',
          savedMessage: '',
          visibilityLevel: defaultVisibility,
          initialVisibilityLevel: defaultVisibility,
          isSaving: false,
          isSavingVisibility: false,
          justSaved: false,
          isEditingMessage: true,
        });
        return newMap;
      });

      // Reload sent requests
      loadRequests();
    } catch (error: any) {
      showMessage(getErrorMessage(error, 'friends.messages.failedToSend'));
    }
  };

  const handleUpdateInvitation = async (userId: number) => {
    const invitation = expandedInvitations.get(userId);
    if (!invitation) return;

    // Mark as saving
    setExpandedInvitations(prev => {
      const newMap = new Map(prev);
      newMap.set(userId, { ...invitation, isSaving: true });
      return newMap;
    });

    try {
      // Update the message on the friendship
      await friendsAPI.updatePendingRequest(invitation.friendshipId, invitation.message || undefined);

      // Switch to read-only mode with saved message
      setExpandedInvitations(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, {
          ...invitation,
          isSaving: false,
          savedMessage: invitation.message,
          isEditingMessage: false,
        });
        return newMap;
      });

      // Reload sent requests to show updated message
      loadRequests();
    } catch (error) {
      showMessage(t('friends.messages.updateFailed'));
      // Revert saving state
      setExpandedInvitations(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, { ...invitation, isSaving: false });
        return newMap;
      });
    }
  };

  // Toggle message editing mode
  const handleToggleMessageEdit = (userId: number) => {
    setExpandedInvitations(prev => {
      const newMap = new Map(prev);
      const invitation = newMap.get(userId);
      if (invitation) {
        newMap.set(userId, { ...invitation, isEditingMessage: true });
      }
      return newMap;
    });
  };

  // Cancel message editing (revert to saved message)
  const handleCancelMessageEdit = (userId: number) => {
    setExpandedInvitations(prev => {
      const newMap = new Map(prev);
      const invitation = newMap.get(userId);
      if (invitation) {
        newMap.set(userId, {
          ...invitation,
          message: invitation.savedMessage,
          isEditingMessage: false,
        });
      }
      return newMap;
    });
  };

  const handleCloseInvitation = (userId: number) => {
    setExpandedInvitations(prev => {
      const newMap = new Map(prev);
      newMap.delete(userId);
      return newMap;
    });
    // Remove from suggestions when closing the form
    setSuggestions(prev => prev.filter(s => s.id !== userId));
  };

  // Save visibility rule for a friend
  const handleSaveVisibility = async (friendId: number) => {
    const invitation = expandedInvitations.get(friendId);
    if (!invitation || !user?.address?.id) return;

    // Mark as saving visibility
    setExpandedInvitations(prev => {
      const newMap = new Map(prev);
      newMap.set(friendId, { ...invitation, isSavingVisibility: true });
      return newMap;
    });

    try {
      await visibilityAPI.setIndividualVisibility(
        'permanent',
        user.address.id,
        friendId,
        invitation.visibilityLevel
      );

      showMessage(t('friends.messages.visibilitySaved'));

      // Update initial level to current and mark as just saved (show checkmark)
      setExpandedInvitations(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(friendId);
        if (current) {
          newMap.set(friendId, {
            ...current,
            initialVisibilityLevel: current.visibilityLevel,
            isSavingVisibility: false,
            justSaved: true,
          });
        }
        return newMap;
      });
    } catch (error) {
      showMessage(t('friends.messages.visibilityFailed'));
      setExpandedInvitations(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(friendId);
        if (current) {
          newMap.set(friendId, { ...current, isSavingVisibility: false });
        }
        return newMap;
      });
    }
  };

  const handleAcceptRequest = async (friendshipId: number, request: FriendRequest) => {
    try {
      await friendsAPI.acceptRequest(friendshipId);
      showMessage(t('friends.messages.requestAccepted'));

      // Get default visibility level
      const defaultLevel = user?.address?.default_visibility_level ?? 4;

      // Add to acceptedRequests to show visibility form
      setAcceptedRequests(prev => {
        const newMap = new Map(prev);
        newMap.set(request.user_id, {
          visibilityLevel: defaultLevel,
          initialVisibilityLevel: defaultLevel,
          isSavingVisibility: false,
          justSaved: false,
          request: request
        });
        return newMap;
      });

      // Reload friends list (but keep showing accepted card)
      await loadFriends();
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(r => r.friendship_id !== friendshipId));
    } catch (error) {
      showMessage(t('friends.messages.failedToAccept'));
    }
  };

  // Close accepted request card
  const handleCloseAcceptedRequest = (userId: number) => {
    setAcceptedRequests(prev => {
      const newMap = new Map(prev);
      newMap.delete(userId);
      return newMap;
    });
  };

  // Save visibility for accepted request
  const handleSaveAcceptedVisibility = async (userId: number) => {
    const accepted = acceptedRequests.get(userId);
    if (!accepted || !user?.address?.id) return;

    setAcceptedRequests(prev => {
      const newMap = new Map(prev);
      newMap.set(userId, { ...accepted, isSavingVisibility: true });
      return newMap;
    });

    try {
      await visibilityAPI.setIndividualVisibility('permanent', user.address.id, userId, accepted.visibilityLevel);
      showMessage(t('friends.messages.visibilitySaved'));

      setAcceptedRequests(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, {
          ...accepted,
          initialVisibilityLevel: accepted.visibilityLevel,
          isSavingVisibility: false,
          justSaved: true
        });
        return newMap;
      });
    } catch (error) {
      showMessage(t('friends.messages.visibilityFailed'));
      setAcceptedRequests(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, { ...accepted, isSavingVisibility: false });
        return newMap;
      });
    }
  };

  const handleRejectRequest = async (friendshipId: number) => {
    try {
      await friendsAPI.rejectRequest(friendshipId);
      showMessage(t('friends.messages.requestRejected'));
      loadRequests();
    } catch (error) {
      showMessage(t('friends.messages.failedToReject'));
    }
  };

  // Cancel from sidebar - keeps in sidebar with "Renvoyer" button
  const handleCancelRequest = async (friendshipId: number, userId?: number) => {
    try {
      await friendsAPI.cancelSentRequest(friendshipId);

      // Show success message in sidebar
      setSidebarMessage(t('friends.messages.requestCanceled'));
      setTimeout(() => setSidebarMessage(''), 4000);
      // Add to canceled list to show blue button
      setCanceledRequestIds([...canceledRequestIds, friendshipId]);
      // If userId provided, also close expanded invitation in main area
      if (userId) {
        handleCloseInvitation(userId);
      }
    } catch (error) {
      showMessage(t('friends.messages.failedToCancel'));
    }
  };

  // Cancel from main area (search/suggestions) - removes from sidebar completely
  const handleCancelFromMain = async (friendshipId: number, userId: number) => {
    try {
      await friendsAPI.cancelSentRequest(friendshipId);
      showMessage(t('friends.messages.requestCanceled'));
      // Remove from sentRequests to hide from sidebar
      setSentRequests(prev => prev.filter(req => req.friendship_id !== friendshipId));
      // Close the expanded invitation form
      handleCloseInvitation(userId);
    } catch (error) {
      showMessage(t('friends.messages.failedToCancel'));
    }
  };

  // Helper function to check if a friend request is pending for a user
  const isPendingRequest = (userId: number): FriendRequest | undefined => {
    const request = sentRequests.find(request => request.user_id === userId);
    // If the request was canceled, don't return it
    if (request && canceledRequestIds.includes(request.friendship_id)) {
      return undefined;
    }
    return request;
  };

  // Handle clicking on a sidebar request to open the card in main area
  const handleSidebarRequestClick = async (request: FriendRequest) => {
    // Switch to search tab
    setActiveTab('search');
    // Set search query to find the user
    const fullName = `${request.first_name} ${request.last_name}`;
    setSearchQuery(fullName);

    // Expand the invitation form for this user (in read-only mode if message exists)
    const hasMessage = !!request.request_message;
    setExpandedInvitations(prev => {
      const newMap = new Map(prev);
      newMap.set(request.user_id, {
        friendshipId: request.friendship_id,
        message: request.request_message || '',
        savedMessage: request.request_message || '',
        visibilityLevel: 4,
        initialVisibilityLevel: 4,
        isSaving: false,
        isSavingVisibility: false,
        justSaved: false,
        isEditingMessage: !hasMessage, // Edit mode if no message yet
      });
      return newMap;
    });

    // Call search API to get full user info (city, mutual friends, etc.)
    try {
      const { users } = await userAPI.searchUsersAutocomplete(fullName, 10);
      // Find the specific user in results
      const matchingUser = users.find(u => u.id === request.user_id);
      if (matchingUser) {
        handleSearchWithResults([matchingUser]);
      } else if (users.length > 0) {
        // Fallback: show all results
        handleSearchWithResults(users);
      }
    } catch (error) {
      console.error('Failed to search for user:', error);
      // Fallback: create minimal user object
      const userFromRequest = {
        id: request.user_id,
        email: request.email,
        first_name: request.first_name,
        last_name: request.last_name,
        profile_photo: request.profile_photo || undefined,
        created_at: '',
        updated_at: '',
        _searchResult: {
          id: request.user_id,
          email: request.email,
          first_name: request.first_name,
          last_name: request.last_name,
          profile_photo: request.profile_photo || undefined,
          friendship_status: 'pending_sent' as const,
          mutual_friends_count: 0,
          mutual_friend_names: [],
        },
      } as User;
      setSearchResults([userFromRequest]);
    }

    // Scroll to search results after DOM update
    setTimeout(() => {
      searchResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleRemoveFriend = async (friendId: number) => {
    if (!window.confirm(t('friends.messages.confirmRemove'))) return;

    try {
      await friendsAPI.removeFriend(friendId);
      showMessage(t('friends.messages.friendRemoved'));
      loadFriends();
    } catch (error) {
      showMessage(t('friends.messages.failedToRemove'));
    }
  };

  const handleOpenAddToGroupModal = (friendId: number) => {
    setSelectedFriendForGroup(friendId);
    // Pre-select groups that the friend already belongs to
    const currentGroups = friendGroups.get(friendId);
    const currentGroupIds = currentGroups ? currentGroups.map(g => g.id) : [];
    setSelectedGroupsForFriend(currentGroupIds);
    setInitialGroupsForFriend(currentGroupIds); // Track initial state for change detection
    setShowAddFriendToGroupModal(true);
  };

  // Get selected friend's display name for modal title
  const getSelectedFriendName = (): string => {
    if (!selectedFriendForGroup) return '';
    const friend = friends.find(f => f.user_id === selectedFriendForGroup);
    if (!friend) return '';
    return friend.first_name || friend.email?.split('@')[0] || '';
  };

  // Check if groups selection has changed
  const hasGroupsSelectionChanged = (): boolean => {
    if (selectedGroupsForFriend.length !== initialGroupsForFriend.length) return true;
    const sortedCurrent = [...selectedGroupsForFriend].sort();
    const sortedInitial = [...initialGroupsForFriend].sort();
    return sortedCurrent.some((id, index) => id !== sortedInitial[index]);
  };

  const handleAddFriendToGroups = async () => {
    if (!selectedFriendForGroup) {
      return;
    }

    try {
      setLoading(true);

      // Get current groups for this friend
      const currentGroups = friendGroups.get(selectedFriendForGroup);
      const currentGroupIds = currentGroups ? currentGroups.map(g => g.id) : [];

      // Determine groups to add and remove
      const groupsToAdd = selectedGroupsForFriend.filter(id => !currentGroupIds.includes(id));
      const groupsToRemove = currentGroupIds.filter(id => !selectedGroupsForFriend.includes(id));

      // Add friend to new groups and remove from unchecked groups
      await Promise.all([
        ...groupsToAdd.map((groupId) =>
          friendGroupsAPI.addMembers(groupId, { friend_ids: [selectedFriendForGroup] })
        ),
        ...groupsToRemove.map((groupId) =>
          friendGroupsAPI.removeMember(groupId, selectedFriendForGroup)
        )
      ]);

      await loadGroups();
      await loadFriends();
      setShowAddFriendToGroupModal(false);
      setSelectedGroupsForFriend([]);
      showMessage(t('friends.groups.updated'));
    } catch (error) {
      showMessage(t('friends.groups.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupSelection = (groupId: number) => {
    setSelectedGroupsForFriend((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const getFriendTempLocation = (friendId: number) => {
    // Get all temp locations for this friend
    const friendTempLocs = tempLocations.filter(loc => loc.friend_id === friendId);

    if (friendTempLocs.length === 0) {
      return undefined;
    }

    // Find the one with the shortest duration
    return friendTempLocs.reduce((shortest, current) => {
      const shortestDuration = new Date(shortest.end_datetime).getTime() - new Date(shortest.start_datetime).getTime();
      const currentDuration = new Date(current.end_datetime).getTime() - new Date(current.start_datetime).getTime();

      return currentDuration < shortestDuration ? current : shortest;
    });
  };

  // Groups functions
  const loadGroups = async () => {
    try {
      const { groups: fetchedGroups } = await friendGroupsAPI.getUserGroupsWithMembers();
      setGroups(fetchedGroups);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupData.name.trim()) {
      showMessage(t('friends.groups.enterName'));
      return;
    }

    try {
      setLoading(true);
      await friendGroupsAPI.create(newGroupData);
      await loadGroups();
      await loadFriends(); // Refresh friend group badges
      setNewGroupData({ name: '', color: getRandomListColor() });
      setShowCreateGroupForm(false);
      showMessage(t('friends.groups.created'));
    } catch (error) {
      showMessage(t('friends.groups.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroupAndAddFriend = async () => {
    if (!newGroupInModal.name.trim()) {
      showMessage(t('friends.groups.enterName'));
      return;
    }

    if (!selectedFriendForGroup) return;

    try {
      setLoading(true);
      // Create the group
      const response = await friendGroupsAPI.create(newGroupInModal);
      const newGroupId = response.group.id;

      // Add the friend to the new group
      await friendGroupsAPI.addMembers(newGroupId, { friend_ids: [selectedFriendForGroup] });

      // Reload data
      await loadGroups();
      await loadFriends();

      // Reset form and close creation panel, but keep modal open
      setNewGroupInModal({ name: '', color: getRandomListColor() });
      setShowCreateGroupInModal(false);
      // Add the new group to selected groups to show it was added
      setSelectedGroupsForFriend([...selectedGroupsForFriend, newGroupId]);

      showMessage(t('friends.groups.createdAndAdded'));
    } catch (error) {
      showMessage(t('friends.groups.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handler for AddToListsModal's onCreateGroup
  const handleCreateGroupFromModal = async (name: string, color: string) => {
    if (!selectedFriendForGroup) return;

    try {
      // Create the group
      const response = await friendGroupsAPI.create({ name, color });
      const newGroupId = response.group.id;

      // Add the friend to the new group
      await friendGroupsAPI.addMembers(newGroupId, { friend_ids: [selectedFriendForGroup] });

      // Reload data
      await loadGroups();
      await loadFriends();

      // Add the new group to selected groups to show it was added
      setSelectedGroupsForFriend(prev => [...prev, newGroupId]);

      showMessage(t('friends.messages.groupCreated'));
    } catch (error) {
      showMessage(t('friends.messages.error'));
      throw error; // Re-throw so the modal knows it failed
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!window.confirm(t('friends.groups.confirmDeleteFull'))) {
      return;
    }

    try {
      setLoading(true);
      await friendGroupsAPI.delete(groupId);
      await loadGroups();
      await loadFriends(); // Refresh friend group badges
      showMessage(t('friends.groups.deleted'));
    } catch (error) {
      showMessage(t('friends.groups.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditGroup = (group: FriendGroupWithMembers) => {
    setEditingGroupId(group.id);
    setEditGroupData({ name: group.name, color: group.color || '#4a90e2' });
    setOpenMenuGroupId(null); // Close menu when editing
  };

  const handleCancelEditGroup = () => {
    setEditingGroupId(null);
    setEditGroupData({ name: '', color: '#4a90e2' });
  };

  const handleToggleGroupMenu = (groupId: number) => {
    setOpenMenuGroupId(openMenuGroupId === groupId ? null : groupId);
  };

  const handleShowGroupOnMap = (groupId: number) => {
    // Store the group filter in localStorage and navigate to map
    localStorage.setItem('mapGroupFilter', groupId.toString());
    navigate('/map');
  };

  const handleToggleFriendMenu = (friendId: number) => {
    setOpenMenuFriendId(openMenuFriendId === friendId ? null : friendId);
  };

  const handleShowOfficialAddress = (friendId: number) => {
    // Show friend's official address on map
    localStorage.setItem('mapFriendFilter', friendId.toString());
    localStorage.setItem('mapAddressType', 'official');
    localStorage.setItem('openPopup', 'true');
    navigate('/map');
  };

  const handleShowCurrentAddress = (friendId: number) => {
    // Get the active temporary location with shortest duration
    const friendTempLocation = getFriendTempLocation(friendId);

    if (!friendTempLocation) {
      showMessage(t('friends.noCurrentAddress'));
      return;
    }

    // Show friend's current temporary location on map
    localStorage.setItem('mapFriendFilter', friendId.toString());
    localStorage.setItem('mapAddressType', 'current');
    localStorage.setItem('mapTempLocationId', friendTempLocation.id.toString());
    localStorage.setItem('openPopup', 'true');
    navigate('/map');
  };

  const handleViewFriendProfile = (friendId: number) => {
    // Navigate to map with friend filter active and panel open
    localStorage.setItem('mapSelectedFriend', friendId.toString());
    localStorage.setItem('openFriendPanel', 'true');
    navigate('/map');
  };

  const handleBlacklistFriend = async (friendId: number) => {
    if (!window.confirm(t('friends.confirmBlacklist'))) {
      return;
    }

    try {
      // Call API to blacklist the friend
      // await friendsAPI.blacklistFriend(friendId);
      showMessage(t('friends.blacklistSuccess'));
      setOpenMenuFriendId(null);
      // Optionally refresh the friends list
      await loadFriends();
    } catch (error) {
      console.error('Failed to blacklist friend:', error);
      showMessage(t('friends.blacklistError'));
    }
  };

  const handleSaveEditGroup = async () => {
    if (!editingGroupId || !editGroupData.name.trim()) {
      showMessage(t('friends.groups.enterName'));
      return;
    }

    try {
      setLoading(true);
      await friendGroupsAPI.update(editingGroupId, editGroupData);
      await loadGroups();
      await loadFriends();
      setEditingGroupId(null);
      setEditGroupData({ name: '', color: '#4a90e2' });
      showMessage(t('friends.groups.updated'));
    } catch (error) {
      showMessage(t('friends.groups.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddMembersModal = (groupId: number) => {
    setSelectedGroupId(groupId);
    setSelectedFriendIds([]);
    setShowAddMembersModal(true);
  };

  const handleAddMembers = async () => {
    if (!selectedGroupId || selectedFriendIds.length === 0) {
      showMessage(t('friends.groups.selectFriend'));
      return;
    }

    try {
      setLoading(true);
      await friendGroupsAPI.addMembers(selectedGroupId, { friend_ids: selectedFriendIds });
      await loadGroups();
      await loadFriends(); // Refresh friend group badges
      setShowAddMembersModal(false);
      setSelectedFriendIds([]);
      showMessage(t('friends.groups.friendsAdded'));
    } catch (error) {
      showMessage(t('friends.groups.addFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (groupId: number, friendId: number) => {
    if (!window.confirm(t('friends.groups.confirmRemoveMember'))) {
      return;
    }

    try {
      setLoading(true);
      await friendGroupsAPI.removeMember(groupId, friendId);
      await loadGroups();
      await loadFriends(); // Refresh friend group badges
      showMessage(t('friends.groups.memberRemoved'));
    } catch (error) {
      showMessage(t('friends.groups.removeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleFriendSelection = (friendId: number) => {
    setSelectedFriendIds((prev) =>
      prev.includes(friendId)
        ? prev.filter((id) => id !== friendId)
        : [...prev, friendId]
    );
  };

  const getAvailableFriends = () => {
    if (!selectedGroupId) return friends;
    const group = groups.find((g) => g.id === selectedGroupId);
    if (!group) return friends;
    const memberIds = group.members.map((m) => m.id);
    return friends.filter((f) => !memberIds.includes(f.user_id));
  };

  const getPhotoUrl = (photoPath?: string) => {
    if (!photoPath) return null;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${baseUrl.replace(/\/api$/, '')}${photoPath}`;
  };

  return (
    <div className="friends-page">
      <div className="friends-container">
        <h1>{t('friends.title')}</h1>

        {message && <div className="success-message">{message}</div>}

        <div className="tabs" ref={tabsRef}>
          <button
            className={activeTab === 'friends' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('friends')}
          >
            {t('friends.tabs.myFriendsCount', { count: friends.length })}
          </button>
          <button
            className={activeTab === 'groups' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('groups')}
          >
            {t('friends.tabs.groupsCount', { count: groups.length })}
          </button>
          <button
            className={activeTab === 'search' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('search')}
          >
            {t('friends.tabs.findFriends')}
          </button>
        </div>

        {/* Swipeable content area */}
        <div
          className="friends-content"
          ref={contentRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
        {/* Friends List */}
        {activeTab === 'friends' && (
          <div className="friends-section">
            {/* Pending Requests Section */}
            {pendingRequests.length > 0 && (
              <div className="pending-requests-section">
                <h2>{t('friends.requests.pending')}</h2>
                <div className="friends-list">
                  {pendingRequests.map((request) => (
                    <div key={request.friendship_id} className="friend-card pending-request-card">
                      <div className="pending-request-content">
                        <div className="friend-info">
                          {request.profile_photo ? (
                            <img
                              src={getPhotoUrl(request.profile_photo) || undefined}
                              alt={`${request.first_name}`}
                              className="friend-photo"
                            />
                          ) : (
                            <div className="friend-photo-placeholder">
                              {request.first_name[0]}{request.last_name[0]}
                            </div>
                          )}
                          <div className="pending-request-info">
                            <div className="pending-request-header">
                              <h3>
                                {request.first_name} {request.last_name}
                                {request.city && (
                                  <span className="header-location"> - {request.city}{request.country ? `, ${request.country}` : ''}</span>
                                )}
                              </h3>
                            </div>
                            {request.mutual_friends_count && request.mutual_friends_count > 0 && (
                              <p className="mutual-friends-line">
                                <span className="mutual-count">
                                  {request.mutual_friends_count} {request.mutual_friends_count === 1
                                    ? t('friends.search.mutualFriend')
                                    : t('friends.search.mutualFriends')}
                                </span>
                                {request.mutual_friend_names && request.mutual_friend_names.length > 0 && (
                                  <span className="mutual-names">
                                    {request.mutual_friend_names.slice(0, 3).join(', ')}
                                    {request.mutual_friend_names.length > 3 && ` +${request.mutual_friend_names.length - 3}`}
                                  </span>
                                )}
                              </p>
                            )}
                            {request.request_message && (
                              <p className="request-message">
                                <span className="message-icon">💬</span>
                                "{request.request_message}"
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="request-actions-compact">
                          <button
                            className="btn-accept"
                            onClick={() => handleAcceptRequest(request.friendship_id, request)}
                            title={t('friends.requests.accept')}
                          >
                            ✓ {t('friends.requests.accept')}
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => handleRejectRequest(request.friendship_id)}
                            title={t('friends.requests.reject')}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Accepted Requests with Visibility Form */}
            {acceptedRequests.size > 0 && (
              <div className="accepted-requests-section">
                <div className="friends-list">
                  {Array.from(acceptedRequests.entries()).map(([userId, accepted]) => (
                    <div key={userId} className="friend-card accepted-request-card">
                      <div className="accepted-request-content">
                        <div className="friend-info">
                          {accepted.request.profile_photo ? (
                            <img
                              src={getPhotoUrl(accepted.request.profile_photo) || undefined}
                              alt={`${accepted.request.first_name}`}
                              className="friend-photo"
                            />
                          ) : (
                            <div className="friend-photo-placeholder">
                              {accepted.request.first_name[0]}{accepted.request.last_name[0]}
                            </div>
                          )}
                          <div className="accepted-request-info">
                            <div className="accepted-request-header">
                              <h3>
                                {accepted.request.first_name} {accepted.request.last_name}
                                <span className="accepted-badge">✓ {t('friends.requests.accepted')}</span>
                              </h3>
                              <button
                                className="btn-close-accepted"
                                onClick={() => handleCloseAcceptedRequest(userId)}
                                title={t('common.close')}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Visibility form */}
                        <div className="invitation-form-content">
                          <div className="invitation-row-visibility">
                            <span className="visibility-label">{t('friends.invitation.visibilityLabel')}</span>
                            <div className="visibility-buttons">
                              {[1, 2, 3, 4, 5].map(level => (
                                <button
                                  key={level}
                                  type="button"
                                  className={`visibility-btn ${accepted.visibilityLevel === level ? 'active' : ''}`}
                                  onClick={() => {
                                    setAcceptedRequests(prev => {
                                      const newMap = new Map(prev);
                                      newMap.set(userId, { ...accepted, visibilityLevel: level, justSaved: false });
                                      return newMap;
                                    });
                                  }}
                                >
                                  {level}
                                </button>
                              ))}
                            </div>
                            <span className="visibility-description-compact">
                              {t(`friends.invitation.visibilityLevel${accepted.visibilityLevel}`)}
                            </span>
                            {(accepted.justSaved || accepted.visibilityLevel !== accepted.initialVisibilityLevel) && (
                              <button
                                className={`btn-save-visibility ${accepted.justSaved ? 'saved' : ''}`}
                                onClick={() => handleSaveAcceptedVisibility(userId)}
                                disabled={accepted.isSavingVisibility || accepted.justSaved}
                                title={accepted.justSaved ? t('friends.messages.visibilitySaved') : t('friends.invitation.saveVisibility')}
                              >
                                {accepted.isSavingVisibility ? '...' : (
                                  accepted.justSaved ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12"/>
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                      <polyline points="17 21 17 13 7 13 7 21"/>
                                      <polyline points="7 3 7 8 15 8"/>
                                    </svg>
                                  )
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* My Contacts Section */}
            {(pendingRequests.length > 0 || acceptedRequests.size > 0) && friends.length > 0 && (
              <h2 className="my-contacts-title">{t('friends.myFriends.title')}</h2>
            )}

            {/* Friends List */}
            <div className="friends-list">
              {friends.length === 0 ? (
                <p className="empty-message">{t('friends.myFriends.empty')}</p>
              ) : (
                friends.map((friend) => {
                const hasGroups = friendGroups.get(friend.user_id) && friendGroups.get(friend.user_id)!.length > 0;
                return (
                <div key={friend.user_id} className={`friend-card ${openMenuFriendId === friend.user_id ? 'menu-open' : ''}`}>
                  <div className="friend-card-content">
                    <div className="friend-info">
                      {friend.profile_photo && (
                        <img
                          src={getPhotoUrl(friend.profile_photo) || undefined}
                          alt={`${friend.first_name}`}
                          className="friend-photo"
                        />
                      )}
                      <div className="friend-details">
                        <div className="friend-name-container">
                          <h3>
                            {friend.first_name} {friend.last_name}
                          </h3>
                          {user?.address?.id && (
                            <VisibilityBubble
                              friendId={friend.user_id}
                              addressId={user.address.id}
                              currentLevel={friend.visibility_level ?? null}
                              defaultLevel={user.address.default_visibility_level ?? 4}
                              hasCustomRule={friend.has_custom_visibility ?? false}
                              onVisibilityChange={(newLevel) => {
                                setFriends(prev => prev.map(f =>
                                  f.user_id === friend.user_id
                                    ? { ...f, visibility_level: newLevel, has_custom_visibility: newLevel !== null }
                                    : f
                                ));
                              }}
                            />
                          )}
                          {getFriendTempLocation(friend.user_id) && (
                            <span className="traveling-badge" title={`En déplacement à ${getFriendTempLocation(friend.user_id)!.name}`}>
                              ✈️ En déplacement
                            </span>
                          )}
                        </div>
                        {friend.city && <p className="friend-location">{friend.city}, {friend.country}</p>}
                      </div>
                    </div>
                    <div className="friend-groups">
                      {hasGroups && (
                        <>
                          {friendGroups.get(friend.user_id)!.map((group) => (
                            <span
                              key={group.id}
                              className="group-badge"
                              style={{ backgroundColor: group.color || '#4a90e2' }}
                              title={group.name}
                            >
                              {group.name}
                            </span>
                          ))}
                          <button
                            className="btn-add-to-group-circle"
                            onClick={() => handleOpenAddToGroupModal(friend.user_id)}
                            title={t('friends.actions.addToGroup')}
                          >
                            +
                          </button>
                        </>
                      )}
                      {!hasGroups && (
                        <button
                          className="btn-add-to-group"
                          onClick={() => handleOpenAddToGroupModal(friend.user_id)}
                          title={t('friends.actions.addToGroup')}
                        >
                          Liste +
                        </button>
                      )}
                    </div>
                    <div className="friend-menu-container">
                      <button
                        className="btn-friend-menu"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFriendMenu(friend.user_id);
                        }}
                        title={t('friends.groups.friendOptions')}
                      >
                        ⋮
                      </button>
                      {openMenuFriendId === friend.user_id && (
                        <div className="friend-menu-dropdown">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowOfficialAddress(friend.user_id);
                            }}
                          >
                            {t('friends.menu.showOfficialAddress')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowCurrentAddress(friend.user_id);
                            }}
                            disabled={!getFriendTempLocation(friend.user_id)}
                          >
                            {t('friends.menu.showCurrentAddress')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewFriendProfile(friend.user_id);
                            }}
                          >
                            {t('friends.menu.viewProfile')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFriend(friend.user_id);
                            }}
                            className="danger-option"
                          >
                            {t('friends.menu.removeFriend')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBlacklistFriend(friend.user_id);
                            }}
                            className="danger-option"
                          >
                            {t('friends.menu.blacklist')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )})
            )}
            </div>
          </div>
        )}

        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <div className="groups-section">
            <div className="groups-header">
              <button
                className="btn-primary"
                onClick={() => setShowCreateGroupForm(!showCreateGroupForm)}
                disabled={loading}
              >
                {showCreateGroupForm ? t('friends.groups.cancel') : t('friends.groups.createNew')}
              </button>
            </div>

            {showCreateGroupForm && (
              <div className="create-group-form">
                <h3>{t('friends.groups.newGroup')}</h3>
                <div className="form-row-inline">
                  <div className="form-group form-group-flex">
                    <label>{t('friends.groups.groupName')} *</label>
                    <input
                      type="text"
                      value={newGroupData.name}
                      onChange={(e) => setNewGroupData({ ...newGroupData, name: e.target.value })}
                      placeholder={t('friends.groups.namePlaceholder')}
                      maxLength={100}
                    />
                  </div>
                  <div className="form-group form-group-color">
                    <label>{t('friends.groups.color')}</label>
                    <ListColorPicker
                      color={newGroupData.color}
                      onChange={(color) => setNewGroupData({ ...newGroupData, color })}
                      size="medium"
                    />
                  </div>
                </div>
                <button
                  className="btn-primary"
                  onClick={handleCreateGroup}
                  disabled={loading}
                >
                  {loading ? t('friends.groups.creating') : t('friends.groups.create')}
                </button>
              </div>
            )}

            <div className="groups-list">
              {groups.length === 0 ? (
                <div className="empty-message">
                  <p>{t('friends.groups.empty')}</p>
                  <p style={{ fontSize: '14px', color: '#999' }}>{t('friends.groups.emptyDescription')}</p>
                </div>
              ) : (
                groups.map((group) => (
                  <div key={group.id} className="group-card">
                    {editingGroupId === group.id ? (
                      /* Edit Mode */
                      <div className="group-edit-inline">
                        <button
                          className="btn-inline-close"
                          onClick={handleCancelEditGroup}
                          disabled={loading}
                          title={t('common.close')}
                        >
                          ✕
                        </button>
                        <button
                          className="btn-inline-delete"
                          onClick={() => handleDeleteGroup(group.id)}
                          disabled={loading}
                          title={t('friends.groups.deleteGroup')}
                        >
                          {t('friends.groups.deleteGroup')}
                        </button>
                        <ListColorPicker
                          color={editGroupData.color}
                          onChange={(color) => setEditGroupData({ ...editGroupData, color })}
                          size="small"
                        />
                        <input
                          type="text"
                          value={editGroupData.name}
                          onChange={(e) => setEditGroupData({ ...editGroupData, name: e.target.value })}
                          className="inline-group-name-input"
                          placeholder={t('friends.groups.namePlaceholder')}
                        />
                        <button
                          className="btn-inline-create"
                          onClick={handleSaveEditGroup}
                          disabled={loading || !editGroupData.name.trim()}
                          title={t('friends.groups.save')}
                        >
                          ✓
                        </button>
                      </div>
                    ) : (
                      /* Display Mode */
                      <div
                        className="group-header-item"
                        onClick={() => setExpandedGroupId(expandedGroupId === group.id ? null : group.id)}
                      >
                        <span className="group-color-indicator" style={{ backgroundColor: group.color }}></span>
                        <div className="group-info">
                          <h3>{group.name}</h3>
                        </div>
                        <div className="group-actions">
                          <span className="member-count">
                            {group.members.length} {group.members.length === 1 ? t('friends.groups.member') : t('friends.groups.members')}
                          </span>
                          <button
                            className="btn-add-to-group"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAddMembersModal(group.id);
                            }}
                            title={t('friends.groups.addMembers')}
                            disabled={loading}
                          >
                            +
                          </button>
                        </div>
                        <div className="group-menu-container">
                          <button
                            className="btn-group-menu"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleGroupMenu(group.id);
                            }}
                            title={t('friends.groups.listOptions')}
                            disabled={loading}
                          >
                            ⋮
                          </button>
                          {openMenuGroupId === group.id && (
                            <div className="group-menu-dropdown">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEditGroup(group);
                                }}
                                disabled={loading}
                              >
                                {t('friends.groups.editGroup')}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowGroupOnMap(group.id);
                                }}
                                disabled={loading}
                              >
                                {t('friends.groups.showOnMap')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {expandedGroupId === group.id && editingGroupId !== group.id && (
                      <div className="group-members">
                        {group.members.length === 0 ? (
                          <p className="empty-message">{t('friends.groups.noMembers')}</p>
                        ) : (
                          <div className="members-grid">
                            {group.members.map((member) => (
                              <div key={member.id} className="member-card">
                                {member.profile_photo ? (
                                  <img
                                    src={getPhotoUrl(member.profile_photo) || undefined}
                                    alt={`${member.first_name} ${member.last_name}`}
                                    className="member-photo"
                                  />
                                ) : (
                                  <div className="member-photo-placeholder">
                                    {member.first_name[0]}{member.last_name[0]}
                                  </div>
                                )}
                                <div className="member-info">
                                  <p className="member-name">
                                    {member.first_name} {member.last_name}
                                  </p>
                                </div>
                                <button
                                  className="btn-remove-member"
                                  onClick={() => handleRemoveMember(group.id, member.id)}
                                  title={t('friends.groups.confirmRemoveMember')}
                                  disabled={loading}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Find Friends (with suggestions and search) */}
        {activeTab === 'search' && (
          <div className="search-section">
            <div className="find-friends-layout">
              {/* Main Content */}
              <div className="find-friends-main">
                {/* Contact Sync Prompt - show if sync enabled and not synced */}
                {contactSyncEnabled && !contactSyncPromptDismissed && (!contactSyncStatus || !contactSyncStatus.synced) && (
                  <div className="contact-sync-prompt">
                    <button
                      className="contact-sync-prompt-close"
                      onClick={() => setContactSyncPromptDismissed(true)}
                      aria-label="Fermer"
                    >
                      &times;
                    </button>
                    <div className="contact-sync-icon">📱</div>
                    <div className="contact-sync-content">
                      <h3>Retrouve tes amis</h3>
                      {contactSyncError === 'SETTINGS_REQUIRED' ? (
                        <>
                          <p className="contact-sync-instructions">
                            Active l'accès aux contacts :<br />
                            <strong>Paramètres → Applications → Bob Map → Autorisations → Contacts</strong>
                          </p>
                        </>
                      ) : (
                        <p>Synchronise tes contacts pour trouver tes amis déjà sur Bob Map</p>
                      )}
                      {contactSyncError && contactSyncError !== 'SETTINGS_REQUIRED' && (
                        <p className="contact-sync-error">{contactSyncError}</p>
                      )}
                    </div>
                    {contactSyncError === 'SETTINGS_REQUIRED' ? (
                      <button
                        className="btn-sync-contacts"
                        onClick={handleRetrySync}
                      >
                        Réessayer
                      </button>
                    ) : (
                      <button
                        className="btn-sync-contacts"
                        onClick={handleContactSync}
                        disabled={contactSyncLoading}
                      >
                        {contactSyncLoading ? 'Synchronisation...' : 'Synchroniser'}
                      </button>
                    )}
                  </div>
                )}

                {/* Sync disabled hint - show when sync is disabled in settings */}
                {!contactSyncEnabled && (!contactSyncStatus || !contactSyncStatus.synced) && (
                  <div className="contact-sync-disabled-hint">
                    <span>📱 La synchronisation des contacts est désactivée.</span>
                    <a href="/my-data">Activer dans les paramètres</a>
                  </div>
                )}

                {/* Contact Sync Status - show when synced (dismissible) */}
                {contactSyncStatus && contactSyncStatus.synced && !hideSyncStatusCard && (
                  <div className="contact-sync-status">
                    <div className="sync-info">
                      <span className="sync-badge">✓ {contactSyncStatus.contactCount} contacts synchronisés</span>
                      {contactSyncStatus.matchesCount !== undefined && (
                        <span className="sync-matches">
                          {contactSyncStatus.matchesCount > 0
                            ? `🎉 ${contactSyncStatus.matchesCount} ami(s) trouvé(s) sur Bob Map !`
                            : `Aucun contact sur Bob Map - invite tes amis !`}
                        </span>
                      )}
                    </div>
                    <button
                      className="btn-close-sync-status"
                      onClick={() => {
                        sessionStorage.setItem('hideSyncStatus', 'true');
                        setHideSyncStatusCard(true);
                      }}
                      title="Fermer"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Search Form with Autocomplete */}
                <div className="search-form-section" ref={searchContainerRef}>
                  <form onSubmit={handleSearch} className="search-form">
                    <div className="search-input-container">
                      <input
                        type="text"
                        placeholder={t('friends.search.placeholder')}
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onFocus={() => searchQuery.length >= 2 && autocompleteSuggestions.length > 0 && setShowAutocomplete(true)}
                        onKeyDown={handleSearchKeyDown}
                        className="search-input"
                        autoComplete="off"
                      />
                      {/* Autocomplete Dropdown */}
                      {showAutocomplete && (
                        <div className="autocomplete-dropdown">
                          {autocompleteLoading ? (
                            <div className="autocomplete-loading">
                              {t('friends.search.searching')}...
                            </div>
                          ) : autocompleteSuggestions.length === 0 ? (
                            <div className="autocomplete-empty">
                              {t('friends.search.noResults')}
                            </div>
                          ) : (
                            autocompleteSuggestions.map((user, index) => (
                              <div
                                key={user.id}
                                className={`autocomplete-item ${index === selectedAutocompleteIndex ? 'selected' : ''}`}
                                onClick={() => handleAutocompleteSelect(user)}
                              >
                                <div className="autocomplete-item-left">
                                  {user.profile_photo ? (
                                    <img
                                      src={getPhotoUrl(user.profile_photo) || undefined}
                                      alt={`${user.first_name}`}
                                      className="autocomplete-photo"
                                    />
                                  ) : (
                                    <div className="autocomplete-photo-placeholder">
                                      {user.first_name[0]}{user.last_name[0]}
                                    </div>
                                  )}
                                  <div className="autocomplete-info">
                                    <span className="autocomplete-name">
                                      {user.first_name} {user.last_name}
                                    </span>
                                    <span className="autocomplete-meta">
                                      {user.city && (
                                        <span className="autocomplete-location">
                                          {user.city}{user.country ? `, ${user.country}` : ''}
                                        </span>
                                      )}
                                      {user.match_type && user.match_type !== 'name' && (
                                        <span className="autocomplete-match-type">
                                          {t(`friends.search.matchType.${user.match_type}`)}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <div className="autocomplete-status">
                                  {user.friendship_status === 'friend' ? (
                                    <span className="status-friend">✓ {t('friends.search.isFriend')}</span>
                                  ) : user.friendship_status === 'pending_sent' ? (
                                    <span className="status-pending">{t('friends.search.requestPending')}</span>
                                  ) : user.friendship_status === 'pending_received' ? (
                                    <span className="status-pending-received">{t('friends.search.requestReceived')}</span>
                                  ) : user.mutual_friends_count > 0 ? (
                                    <span className="status-mutual">
                                      {user.mutual_friends_count} {user.mutual_friends_count === 1
                                        ? t('friends.search.mutualFriend')
                                        : t('friends.search.mutualFriends')}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? t('friends.search.searching') : t('friends.search.button')}
                    </button>
                  </form>

                  {searchResults.length > 0 && (
                    <div ref={searchResultsRef} className="friends-list search-results-list">
                      {searchResults.map((user: any) => {
                        const searchResult = user._searchResult as UserSearchResult | undefined;
                        const pendingRequest = isPendingRequest(user.id);
                        const isFriend = searchResult?.friendship_status === 'friend';
                        const isPendingSent = searchResult?.friendship_status === 'pending_sent';
                        const isPendingReceived = searchResult?.friendship_status === 'pending_received';
                        const expandedInvitation = expandedInvitations.get(user.id);

                        // Create display invitation for pending requests (auto-show form)
                        const hasExistingMessage = !!pendingRequest?.request_message;
                        const displayInvitation = expandedInvitation || (pendingRequest ? {
                          friendshipId: pendingRequest.friendship_id,
                          message: pendingRequest.request_message || '',
                          savedMessage: pendingRequest.request_message || '',
                          visibilityLevel: 4,
                          initialVisibilityLevel: 4,
                          isSaving: false,
                          isSavingVisibility: false,
                          justSaved: false,
                          isEditingMessage: !hasExistingMessage,
                        } : null);

                        // Helper to ensure expandedInvitation exists before updating
                        const ensureExpanded = () => {
                          if (!expandedInvitation && pendingRequest) {
                            setExpandedInvitations(prev => {
                              const newMap = new Map(prev);
                              newMap.set(user.id, {
                                friendshipId: pendingRequest.friendship_id,
                                message: pendingRequest.request_message || '',
                                savedMessage: pendingRequest.request_message || '',
                                visibilityLevel: 4,
                                initialVisibilityLevel: 4,
                                isSaving: false,
                                isSavingVisibility: false,
                                justSaved: false,
                                isEditingMessage: !hasExistingMessage,
                              });
                              return newMap;
                            });
                          }
                        };

                        return (
                          <div key={user.id} className={`friend-card search-result-card ${displayInvitation ? 'expanded-invitation' : ''}`}>
                            <div className="friend-info">
                              {user.profile_photo ? (
                                <img
                                  src={getPhotoUrl(user.profile_photo) || undefined}
                                  alt={`${user.first_name}`}
                                  className="friend-photo"
                                />
                              ) : (
                                <div className="friend-photo-placeholder">
                                  {user.first_name[0]}{user.last_name[0]}
                                </div>
                              )}
                              <div className="search-result-info">
                                {/* Line 1: Name - X mutual friends */}
                                <div className="search-result-header">
                                  <span className="search-result-name">
                                    {user.first_name} {user.last_name}
                                  </span>
                                  {isFriend && <span className="friend-badge">✓ {t('friends.search.isFriend')}</span>}
                                  {searchResult && searchResult.mutual_friends_count > 0 && !isFriend && (
                                    <span className="search-result-mutual">
                                      - {searchResult.mutual_friends_count} {searchResult.mutual_friends_count === 1
                                        ? t('friends.search.mutualFriend')
                                        : t('friends.search.mutualFriends')}
                                    </span>
                                  )}
                                </div>
                                {/* Line 2: City, Country */}
                                {user.address && (
                                  <p className="search-result-location">
                                    {user.address.city}, {user.address.country}
                                  </p>
                                )}
                                {/* Line 3: Mutual friend names */}
                                {searchResult && searchResult.mutual_friend_names && searchResult.mutual_friend_names.length > 0 && !isFriend && (
                                  <p className="search-result-mutual-names">
                                    {t('friends.search.friendsWith')} {searchResult.mutual_friend_names.slice(0, 3).join(', ')}
                                    {searchResult.mutual_friend_names.length > 3 && ` +${searchResult.mutual_friend_names.length - 3}`}
                                  </p>
                                )}
                                {isPendingReceived && (
                                  <p className="request-received-text">
                                    {t('friends.search.requestReceived')}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Action buttons area */}
                            {displayInvitation ? (
                              <button
                                key="cancel"
                                className="btn-cancel-request-text"
                                onClick={() => handleCancelFromMain(
                                  displayInvitation.friendshipId,
                                  user.id
                                )}
                              >
                                {t('friends.invitation.cancel')}
                              </button>
                            ) : isFriend ? (
                              <button
                                key="view"
                                className="btn-view-profile"
                                onClick={() => handleViewFriendProfile(user.id)}
                              >
                                {t('friends.menu.viewProfile')}
                              </button>
                            ) : isPendingReceived ? (
                              <span key="received" className="pending-badge">{t('friends.search.pendingResponse')}</span>
                            ) : (
                              <button
                                key="add"
                                className="btn-primary"
                                onClick={() => handleSendRequest(user.id)}
                              >
                                {t('friends.search.addFriend')}
                              </button>
                            )}

                            {/* Expanded invitation form content */}
                            {displayInvitation && (
                              <div className="invitation-form-content">
                                {/* Row 1: Visibility label + buttons + description + save button aligned right */}
                                <div className="invitation-row-visibility">
                                  <span className="visibility-label">{t('friends.invitation.visibilityLabel')}</span>
                                  <div className="visibility-buttons">
                                    {[1, 2, 3, 4, 5].map(level => (
                                      <button
                                        key={level}
                                        type="button"
                                        className={`visibility-btn ${displayInvitation.visibilityLevel === level ? 'active' : ''}`}
                                        onClick={() => {
                                          ensureExpanded();
                                          setExpandedInvitations(prev => {
                                            const newMap = new Map(prev);
                                            const current = newMap.get(user.id) || displayInvitation;
                                            newMap.set(user.id, { ...current, visibilityLevel: level, justSaved: false });
                                            return newMap;
                                          });
                                        }}
                                      >
                                        {level}
                                      </button>
                                    ))}
                                  </div>
                                  <span className="visibility-description-compact">
                                    {t(`friends.invitation.visibilityLevel${displayInvitation.visibilityLevel}`)}
                                  </span>
                                  {(displayInvitation.justSaved || displayInvitation.visibilityLevel !== displayInvitation.initialVisibilityLevel) && (
                                    <button
                                      className={`btn-save-visibility ${displayInvitation.justSaved ? 'saved' : ''}`}
                                      onClick={() => {
                                        ensureExpanded();
                                        handleSaveVisibility(user.id);
                                      }}
                                      disabled={displayInvitation.isSavingVisibility || displayInvitation.justSaved}
                                      title={displayInvitation.justSaved ? t('friends.messages.visibilitySaved') : t('friends.invitation.saveVisibility')}
                                    >
                                      {displayInvitation.isSavingVisibility ? '...' : (
                                        displayInvitation.justSaved ? (
                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12"/>
                                          </svg>
                                        ) : (
                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                            <polyline points="17 21 17 13 7 13 7 21"/>
                                            <polyline points="7 3 7 8 15 8"/>
                                          </svg>
                                        )
                                      )}
                                    </button>
                                  )}
                                </div>

                                {/* Row 2: Message + Edit/Send buttons */}
                                <div className="invitation-row-message">
                                  <label className="message-label">{t('friends.invitation.message')}</label>
                                  <div className="message-input-group">
                                    <input
                                      type="text"
                                      placeholder={t('friends.invitation.messagePlaceholder')}
                                      value={displayInvitation.message}
                                      onChange={(e) => {
                                        ensureExpanded();
                                        setExpandedInvitations(prev => {
                                          const newMap = new Map(prev);
                                          const current = newMap.get(user.id) || displayInvitation;
                                          newMap.set(user.id, { ...current, message: e.target.value });
                                          return newMap;
                                        });
                                      }}
                                      maxLength={500}
                                      disabled={!displayInvitation.isEditingMessage}
                                      className={!displayInvitation.isEditingMessage ? 'readonly' : ''}
                                    />
                                    {displayInvitation.isEditingMessage ? (
                                      <>
                                        {displayInvitation.savedMessage && (
                                          <button
                                            className="btn-cancel-edit"
                                            onClick={() => handleCancelMessageEdit(user.id)}
                                            title={t('common.cancel')}
                                          >
                                            ✕
                                          </button>
                                        )}
                                        <button
                                          className="btn-send-message"
                                          onClick={() => {
                                            ensureExpanded();
                                            handleUpdateInvitation(user.id);
                                          }}
                                          disabled={displayInvitation.isSaving}
                                          title={t('friends.invitation.update')}
                                        >
                                          {displayInvitation.isSaving ? '...' : '➤'}
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="btn-edit-message"
                                          onClick={() => handleToggleMessageEdit(user.id)}
                                          title={t('common.edit')}
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                            <polyline points="17 21 17 13 7 13 7 21"/>
                                            <polyline points="7 3 7 8 15 8"/>
                                          </svg>
                                        </button>
                                        <button
                                          className="btn-send-message sent"
                                          disabled
                                          title={t('friends.messages.invitationUpdated')}
                                        >
                                          ✓
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Invite Prompt - show when search has been performed and no/few results */}
                {searchQuery.trim().length >= 2 && searchResults.length < 3 && (
                  <div className="invite-prompt">
                    <div className="invite-prompt-content">
                      <span className="invite-icon">✉️</span>
                      <div className="invite-text">
                        <strong>Pas trouvé(e) ?</strong>
                        <span>Invite tes amis à rejoindre Bob Map !</span>
                      </div>
                    </div>
                    <button
                      className="btn-invite"
                      onClick={() => {
                        // Copy invite link to clipboard
                        const inviteUrl = 'https://bob-map.com';
                        const inviteText = `Rejoins-moi sur Bob Map ! Une app pour retrouver tes amis sur la carte. ${inviteUrl}`;
                        if (navigator.share) {
                          navigator.share({
                            title: 'Bob Map',
                            text: inviteText,
                            url: inviteUrl
                          }).catch(() => {
                            navigator.clipboard.writeText(inviteText);
                            setMessage('Lien copié !');
                          });
                        } else {
                          navigator.clipboard.writeText(inviteText);
                          setMessage('Lien copié !');
                        }
                      }}
                    >
                      Inviter
                    </button>
                  </div>
                )}

                {/* Friend Suggestions */}
                {!loadingSuggestions && suggestions.length > 0 && (
                  <div className="suggestions-section">
                    <h2>{t('friends.suggestions.title') || 'Personnes que vous pourriez connaître'}</h2>
                    <div className="suggestions-grid">
                      {suggestions.slice(0, displayedSuggestionsCount).map((suggestion) => {
                        const getInitials = (firstName: string, lastName: string) => {
                          return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
                        };
                        const expandedInvitation = expandedInvitations.get(suggestion.id);
                        const pendingRequest = isPendingRequest(suggestion.id);

                        // Create display invitation for pending requests (auto-show form)
                        const hasSuggestionMessage = !!pendingRequest?.request_message;
                        const displayInvitation = expandedInvitation || (pendingRequest ? {
                          friendshipId: pendingRequest.friendship_id,
                          message: pendingRequest.request_message || '',
                          savedMessage: pendingRequest.request_message || '',
                          visibilityLevel: 4,
                          initialVisibilityLevel: 4,
                          isSaving: false,
                          isSavingVisibility: false,
                          justSaved: false,
                          isEditingMessage: !hasSuggestionMessage,
                        } : null);

                        // Helper to ensure expandedInvitation exists before updating
                        const ensureExpanded = () => {
                          if (!expandedInvitation && pendingRequest) {
                            setExpandedInvitations(prev => {
                              const newMap = new Map(prev);
                              newMap.set(suggestion.id, {
                                friendshipId: pendingRequest.friendship_id,
                                message: pendingRequest.request_message || '',
                                savedMessage: pendingRequest.request_message || '',
                                visibilityLevel: 4,
                                initialVisibilityLevel: 4,
                                isSaving: false,
                                isSavingVisibility: false,
                                justSaved: false,
                                isEditingMessage: !hasSuggestionMessage,
                              });
                              return newMap;
                            });
                          }
                        };

                        return (
                          <div key={suggestion.id} className={`suggestion-card ${displayInvitation ? 'expanded-invitation' : ''}`}>
                            <div className="suggestion-photo">
                              {suggestion.profile_photo ? (
                                <img
                                  src={getPhotoUrl(suggestion.profile_photo) || undefined}
                                  alt={`${suggestion.first_name} ${suggestion.last_name}`}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.classList.remove('hidden');
                                  }}
                                />
                              ) : null}
                              <div className={suggestion.profile_photo ? 'initials-fallback hidden' : 'initials-fallback'}>
                                {getInitials(suggestion.first_name, suggestion.last_name)}
                              </div>
                            </div>

                            <div className="suggestion-info">
                              <div className="suggestion-header">
                                <h3>{suggestion.first_name} {suggestion.last_name}</h3>
                                <p className="suggestion-location">- {suggestion.city}, {suggestion.country}</p>
                              </div>

                              {!displayInvitation && (
                                <div className="suggestion-details">
                                  {suggestion.mutual_friends_count > 0 && (
                                    <span className="badge badge-mutual">
                                      👥 {suggestion.mutual_friends_count} {suggestion.mutual_friends_count === 1 ? 'ami commun' : 'amis communs'}
                                    </span>
                                  )}
                                  {suggestion.is_contact && (
                                    <span className="badge badge-contact">📱 Contact</span>
                                  )}
                                  {suggestion.same_city && (
                                    <span className="badge badge-location">🏠 Même ville</span>
                                  )}
                                  {suggestion.mutual_friend_names && suggestion.mutual_friend_names.length > 0 && (
                                    <span className="mutual-names">
                                      Ami(e) avec {suggestion.mutual_friend_names.slice(0, 2).join(', ')}
                                      {suggestion.mutual_friend_names.length > 2 && ` et ${suggestion.mutual_friend_names.length - 2} autre${suggestion.mutual_friend_names.length > 3 ? 's' : ''}`}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Action buttons */}
                            {displayInvitation ? (
                              <button
                                key="cancel"
                                className="btn-cancel-request-text"
                                onClick={() => handleCancelFromMain(displayInvitation.friendshipId, suggestion.id)}
                              >
                                {t('friends.invitation.cancel')}
                              </button>
                            ) : (
                              <button
                                key="add"
                                className="btn-add-friend"
                                onClick={() => handleSendRequest(suggestion.id)}
                              >
                                {t('friends.search.addFriend')}
                              </button>
                            )}

                            {/* Expanded invitation form content */}
                            {displayInvitation && (
                              <div className="invitation-form-content">
                                {/* Row 1: Visibility label + buttons + description + save button aligned right */}
                                <div className="invitation-row-visibility">
                                  <span className="visibility-label">{t('friends.invitation.visibilityLabel')}</span>
                                  <div className="visibility-buttons">
                                    {[1, 2, 3, 4, 5].map(level => (
                                      <button
                                        key={level}
                                        type="button"
                                        className={`visibility-btn ${displayInvitation.visibilityLevel === level ? 'active' : ''}`}
                                        onClick={() => {
                                          ensureExpanded();
                                          setExpandedInvitations(prev => {
                                            const newMap = new Map(prev);
                                            const current = newMap.get(suggestion.id) || displayInvitation;
                                            newMap.set(suggestion.id, { ...current, visibilityLevel: level, justSaved: false });
                                            return newMap;
                                          });
                                        }}
                                      >
                                        {level}
                                      </button>
                                    ))}
                                  </div>
                                  <span className="visibility-description-compact">
                                    {t(`friends.invitation.visibilityLevel${displayInvitation.visibilityLevel}`)}
                                  </span>
                                  {(displayInvitation.justSaved || displayInvitation.visibilityLevel !== displayInvitation.initialVisibilityLevel) && (
                                    <button
                                      className={`btn-save-visibility ${displayInvitation.justSaved ? 'saved' : ''}`}
                                      onClick={() => {
                                        ensureExpanded();
                                        handleSaveVisibility(suggestion.id);
                                      }}
                                      disabled={displayInvitation.isSavingVisibility || displayInvitation.justSaved}
                                      title={displayInvitation.justSaved ? t('friends.messages.visibilitySaved') : t('friends.invitation.saveVisibility')}
                                    >
                                      {displayInvitation.isSavingVisibility ? '...' : (
                                        displayInvitation.justSaved ? (
                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12"/>
                                          </svg>
                                        ) : (
                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                            <polyline points="17 21 17 13 7 13 7 21"/>
                                            <polyline points="7 3 7 8 15 8"/>
                                          </svg>
                                        )
                                      )}
                                    </button>
                                  )}
                                </div>

                                {/* Row 2: Message + Edit/Send buttons */}
                                <div className="invitation-row-message">
                                  <label className="message-label">{t('friends.invitation.message')}</label>
                                  <div className="message-input-group">
                                    <input
                                      type="text"
                                      placeholder={t('friends.invitation.messagePlaceholder')}
                                      value={displayInvitation.message}
                                      onChange={(e) => {
                                        ensureExpanded();
                                        setExpandedInvitations(prev => {
                                          const newMap = new Map(prev);
                                          const current = newMap.get(suggestion.id) || displayInvitation;
                                          newMap.set(suggestion.id, { ...current, message: e.target.value });
                                          return newMap;
                                        });
                                      }}
                                      maxLength={500}
                                      disabled={!displayInvitation.isEditingMessage}
                                      className={!displayInvitation.isEditingMessage ? 'readonly' : ''}
                                    />
                                    {displayInvitation.isEditingMessage ? (
                                      <>
                                        {displayInvitation.savedMessage && (
                                          <button
                                            className="btn-cancel-edit"
                                            onClick={() => handleCancelMessageEdit(suggestion.id)}
                                            title={t('common.cancel')}
                                          >
                                            ✕
                                          </button>
                                        )}
                                        <button
                                          className="btn-send-message"
                                          onClick={() => {
                                            ensureExpanded();
                                            handleUpdateInvitation(suggestion.id);
                                          }}
                                          disabled={displayInvitation.isSaving}
                                          title={t('friends.invitation.update')}
                                        >
                                          {displayInvitation.isSaving ? '...' : '➤'}
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="btn-edit-message"
                                          onClick={() => handleToggleMessageEdit(suggestion.id)}
                                          title={t('common.edit')}
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                            <polyline points="17 21 17 13 7 13 7 21"/>
                                            <polyline points="7 3 7 8 15 8"/>
                                          </svg>
                                        </button>
                                        <button
                                          className="btn-send-message sent"
                                          disabled
                                          title={t('friends.messages.invitationUpdated')}
                                        >
                                          ✓
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Load More Button */}
                    {displayedSuggestionsCount < suggestions.length && (
                      <div className="load-more-suggestions">
                        <button className="btn-load-more" onClick={handleLoadMoreSuggestions}>
                          Afficher plus de suggestions ({suggestions.length - displayedSuggestionsCount} restantes)
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sidebar - Sent Requests (always visible) */}
              <div className="sent-requests-sidebar">
                <h2>{t('friends.requests.sent') || 'Demandes envoyées'}</h2>
                {sidebarMessage && (
                  <div className="sidebar-success-message">
                    {sidebarMessage}
                  </div>
                )}
                <div className="sidebar-list">
                  {sentRequests.length === 0 ? (
                    <p className="sidebar-empty-message">{t('friends.requests.noSentRequests') || 'Pas de demandes en cours'}</p>
                  ) : (
                    sentRequests.map((request) => {
                      const isCanceled = canceledRequestIds.includes(request.friendship_id);
                      return (
                        <div key={request.friendship_id} className="sidebar-card">
                          <div
                            className="sidebar-card-clickable"
                            onClick={() => !isCanceled && handleSidebarRequestClick(request)}
                            style={{ cursor: isCanceled ? 'default' : 'pointer' }}
                          >
                            {request.profile_photo ? (
                              <img
                                src={getPhotoUrl(request.profile_photo) || undefined}
                                alt={`${request.first_name}`}
                                className="sidebar-photo"
                              />
                            ) : (
                              <div className="sidebar-photo-placeholder">
                                {request.first_name[0]}{request.last_name[0]}
                              </div>
                            )}
                            <div className="sidebar-info">
                              <h4>{request.first_name} {request.last_name}</h4>
                              {!isCanceled && (
                                <p className="pending-badge">{t('friends.requests.pendingStatus')}</p>
                              )}
                            </div>
                          </div>
                          {isCanceled ? (
                            <button
                              className="btn-sidebar-action"
                              onClick={() => handleSendRequest(request.user_id)}
                            >
                              Renvoyer
                            </button>
                          ) : (
                            <button
                              className="btn-cancel-sidebar"
                              onClick={() => handleCancelRequest(request.friendship_id, request.user_id)}
                              title={t('friends.search.cancelRequestTooltip')}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>{/* End swipeable content */}
      </div>

      {/* Add Members Modal */}
      <AddMembersModal
        isOpen={showAddMembersModal}
        onClose={() => setShowAddMembersModal(false)}
        friends={getAvailableFriends().map(f => ({
          id: f.user_id,
          first_name: f.first_name,
          last_name: f.last_name,
          profile_photo: f.profile_photo
        }))}
        selectedIds={selectedFriendIds}
        onToggleSelection={toggleFriendSelection}
        onConfirm={handleAddMembers}
        loading={loading}
        getPhotoUrl={getPhotoUrl}
      />

      {/* Add Friend to Group Modal */}
      <AddToListsModal
        isOpen={showAddFriendToGroupModal}
        onClose={() => setShowAddFriendToGroupModal(false)}
        friendName={getSelectedFriendName()}
        groups={groups.map(g => ({
          id: g.id,
          name: g.name,
          color: g.color
        }))}
        selectedGroupIds={selectedGroupsForFriend}
        onToggleSelection={toggleGroupSelection}
        onConfirm={handleAddFriendToGroups}
        onCreateGroup={handleCreateGroupFromModal}
        loading={loading}
        hasChanges={hasGroupsSelectionChanged()}
      />
    </div>
  );
};

export default Friends;
